import { Pass, Seq, Choice, Cont, Dep, ChangeForm,  setObjectValue } from "../../framework/Processes";
import { Application, External } from "../../framework/comp/Lambda/Constructors";
import { FunctionType } from "../../framework/comp/Types";
import MultivariateFunction from "../../framework/comp/EInstances/MultivariateFunction";
import MenuProcess from "./MenuProcess";
import { currentScore, maxAvailableScore, calcScore, calcMaxScore} from "../score";
import { getNonCompTasks } from "../utility/getNonCompTasks";
import recoveryFromHistory from "../recoveryFromHistory";
import { resetEmotionState } from "../../emotionModel";
import React from "react";

const technicalGameTypes = ["Intro", "Greeting", "Final", "End"];

/**
 * Convert a minigame to a process.
 * @param {Minigame} minigame - a minigame
 * @param {Process} [nextProcess] - continuation (next process to run)
 * @return {Process} a process that corresponds to the minigame attached to a subject "Player"
 */
function AtomicMinigameProcess(minigame, nextProcess = Pass, config = {type: "stub"}, ret) {
	return Dep(External(function(state) {
		const history = state.objects.history;
		const nextAttemptMsg = "Сейчас вам будет повторно предложена задача, на которую вы ранее дали неправильный ответ.";
		const firstAttempt = minigame.silent || !history.filter(({ minigame: { name }, finished }) => name === minigame.name && !finished).length;
		const changeFormProc = Dep(External(function(state) {
			const history = state.objects.history;
			const revHistory = history.slice().reverse();
			const roundCount = history.filter(elem => elem.minigame.name === "final").length + 1;
			const isGameInProgress = state.objects.init && !state.objects.endofGame;
			const isRoundInProgress = state.objects.init && !(technicalGameTypes.includes(minigame.minigameType));
			const form = {
				type: "GameFrame",
				emotionState: resetEmotionState(state.objects.emotionState),
				score: { 
					current: currentScore(history), 
					max: maxAvailableScore(history),
					old: history.length > 1 ? currentScore(history.slice(0, history.length-1)) : 0
				},
				title: minigame.title,
				messages: minigame.messages && 
					minigame.messages(minigame, state.objects.help),
				subform: minigame.form,
				roundCount: roundCount,
				roundQstCount: { current: calcCurrentQstCount(revHistory, state.objects.curcount), max: state.objects.curcount},
				isGameInProgress: isGameInProgress,
				isRoundInProgress: isRoundInProgress,
				isCompleted: minigame.isCompleted,
				showTime: false
			};
			const continuation = Application(Application(
				MultivariateFunction(MinigameContinuation, 3, ["Process", "Object", "Object"], "Process"),
				External(Seq([minigame.postprocess || Pass, nextProcess]), "Process")),
				External(minigame, "Object"));
			const minigameProc = Cont("Player", form, continuation);
			let menuHistoryList = history.filter(elem => !technicalGameTypes.includes(elem.minigame.minigameType))
				.map((elem) => recoveryFromHistory(elem));
			let menuMinigameList = state.objects.menu.map(mg => setMinigameStatus(mg, history, state.objects.currentMinigame, config));
				// if(config.type === "menu") {menuMinigameList = menuMinigameList.filter(mg => mg.name !== state.objects.currentMinigame)};
			
			let menuObject = {fullList: menuMinigameList, historyList: menuHistoryList};
			const menuReturnProc = Seq([setObjectValue("prcType", "main"), AtomicMinigameProcess(minigame, nextProcess, config, "return")]);
			const menuProc = MenuProcess(menuObject, state.objects.prcType === "menu" ? Pass : menuReturnProc);
			return ChangeForm(Choice([minigameProc, menuProc]), menuFormMap, menuMoveMap);
		}, "Function", FunctionType("Object", "Process")));

		if (firstAttempt) {
			return changeFormProc;
		}
		else {
			return Seq([
				ShowMessage(nextAttemptMsg, minigame, config),
				changeFormProc
			]);
		}
		
	}, "Function", FunctionType("Object", "Process")));
}

const menuFormMap = `
	(function (aform) {
	  if (aform.type !== 'SumForm') return aform;
	  var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();
	  var _aform$subforms = _slicedToArray(aform.subforms, 2),
	      form = _aform$subforms[0],
	      menu = _aform$subforms[1].menu;

	  return Object.assign({}, form, { menu: menu });
	})
`
//`(aform) => { if (aform.type !== 'SumForm') return aform; const { subforms: [ form, { menu } ] } = aform; return { ...form, menu } }`;

const menuMoveMap = `
	(function () {
	  return function (variant) {
	    return variant;
	  }.toString();
	})
`
//`() => ((variant) => variant).toString()`;

const attempts = (name, history) => history.filter(({ minigame }) => minigame.name === name).length;

/**
 * Process of adding a history record.
 * @param {Minigame} minigame - played minigame
 * @param {*} res - result of played minigame (e.g. move of atomic minigame)
 * @return {Process} a process that adds a history record to `state.objects.history`
 */
function HistoryRecord(minigame, res) {
	return Dep(External(function (state) {
		var historyRecord = {
			minigame,
			playerAns: res,
			score: calcScore(minigame, res),
			maxScore: calcMaxScore(minigame, res),
			completed: !minigame.isCompleted || minigame.isCompleted(minigame.form, res),
			finished: !minigame.isCompleted || minigame.isCompleted(minigame.form, res) || (minigame.maxTry === 1)
		};
		const maxAttempts = minigame.maxTry || GiveDefaultMaxAttemp(state);
		const terminate = (!historyRecord.completed && attempts(minigame.name, state.objects.history) >= maxAttempts);
		var test = <div><p>Лимит попыток ответа на вопрос исчерпан!</p><p>Перед повторным прохождением игры рекомендуется повторить тему «{minigame.title}».</p></div>;
		//const msg = `Превышено допустимое количество неправильных ответов! Я рекомендую тебе ещё раз повторить эту тему перед повторным прохождением игры. Обязательно проверь свои знания по теме '${minigame.title}'.`;
		const msg = test;
		let msgProc = Pass;
		if (terminate) {
			historyRecord.completed = false;
			historyRecord.finished = true;
			msgProc = ShowMessage(msg, minigame);
		}
		return Seq([minigame.silent ? Pass : setObjectValue("history", historyRecord, [state.objects.history.length]), msgProc]);
	}, "Function", FunctionType("Object", "Process")));
}

function MinigameContinuation(nextProcess, minigame, res) {
	return Seq([
		HistoryRecord(minigame, res),
		nextProcess,
	]);
}

export function ShowMessage(msg, minigame, config) {
	return AtomicMinigameProcess({
		...minigame,
		form: { type: "AutoFinish" },
		isCompleted: () => true,
		messages: () => ({ onFinish: () => msg }),
		silent: true,
	}, Pass, config, "showmessage");
}

export function GiveDefaultMaxAttemp(state){
	if(state.objects.difficulty === 0) return 3;
	else return 2;
}

function calcCurrentQstCount(history, max){
	let res = history.findIndex(elem => elem.minigame.name === "count-rest");
	if(res === -1){
		res = history.length - history.filter(elem => elem.minigame.minigameType === 'Greeting' || elem.minigame.minigameType === 'Intro').length;
	}
	res++;
	return res > max ? max : res;
}

function setMinigameStatus(minigame, history, currentMinigameName, config){
	minigame.status = "";
	let lastHistoryRecord = history.filter(hr => hr.minigame.name === minigame.name).pop();
	let isAsked = lastHistoryRecord !== undefined;
	let isFinished = isAsked && lastHistoryRecord.finished;
	if(!isFinished){
		if(isAsked){
			minigame.status = "asked";
		}
		if(config.type === "menu"){
			if(minigame.name === config.minigames[0].name){
				minigame.status = "current";
			}
		}
		else if(minigame.name === currentMinigameName){
			minigame.status = "current";
		}
	}
	else {
		minigame.status = lastHistoryRecord.completed ? "finished_good" : "finished_bad";
	}
	if(minigame.components){
		minigame.components.sequence.map(mg => setMinigameStatus(mg, history, currentMinigameName, config))
	}
	return minigame;
}

export default AtomicMinigameProcess;